import { NumericValueOfNotAvailable, vendorId } from '../utils/constants';
import { getVendorId, getWhetherIsForGooglePLA } from '../main/selectors';

import { createSelector } from 'reselect';
import { emptyPriceTableCheck } from '../utils/quantityValidation';
import { internalBrands } from '../main/constants';

export const getPriceTableContentHelper = createSelector(
  getVendorId,
  getWhetherIsForGooglePLA,
  (state) => state.price,
  (_, tntMode) => tntMode,
  (currentVendorId, isForGooglePla, price, tntMode) => {
    const isAmsterdam =
      internalBrands.includes(currentVendorId) && parseInt(currentVendorId, 10) === vendorId.Amsterdam;
    const isAdgPromo = internalBrands.includes(currentVendorId) && parseInt(currentVendorId, 10) === vendorId.Windmill;
    const isWindmillCanada =
      internalBrands.includes(currentVendorId) && parseInt(currentVendorId, 10) === vendorId.WindmillCanada;
    const isWindmillLoggedInUserWithCustomerPricing = (isAdgPromo || isWindmillCanada) && price.isCustomerPrice;
    const isWindmillUserWithoutCustomerPricing = (isAdgPromo || isWindmillCanada) && !price.isCustomerPrice;
    const isAplInternalBrandsButAmsterdamAndWindmill =
      internalBrands.includes(currentVendorId) && !isAmsterdam && !isAdgPromo && !isWindmillCanada;
    const isAplInternalBrandsButWindmill = internalBrands.includes(currentVendorId) && !isAdgPromo && !isWindmillCanada;

    const hasNavInkDiscount = price.hasNavInkDiscount;
    const isClearance = price.isClearance;
    const isEveryDayLowPrice = price.isEveryDayLowPrice;
    const isOnSale =
      price.isCouponApplied && !!price.coupon && price.coupon.isItemLevel && price.freeQuantityRatio === 0;
    const itemPriceCode = price.itemPriceCode || '';
    const priceCodeIsE =
      price.priceTable &&
      price.priceTable.length > 0 &&
      price.priceTable.every((row) => row.priceCode && row.priceCode.toUpperCase() === 'E');
    const showUnitPrice = priceCodeIsE && !isForGooglePla;
    const showDiscountedUnitPrice = hasNavInkDiscount || isClearance || isEveryDayLowPrice || isOnSale;

    /* Hide extra price block rows by default for TNT price block test. */
    //const showSaleTotal = showDiscountedUnitPrice && tntMode;
    const showSaleTotal = false;

    const showFreeQuantity =
      price.isCouponApplied && !!price.coupon && price.coupon.isItemLevel && price.freeQuantityRatio > 0;

    /* Hide extra price block rows by default for TNT price block test. */
    //const showTotalCharge = tntMode ? !isForGooglePla : !priceCodeIsE && !isForGooglePla;
    const showTotalCharge = tntMode ? false : !priceCodeIsE && !isForGooglePla;

    const showSavings =
      ((hasNavInkDiscount || isClearance || isEveryDayLowPrice || isOnSale) &&
        price.priceTable.some((row) => row.discountedUnitPrice !== row.unitPrice)) ||
      showFreeQuantity;
    const showTotalChargeForGooglePla = isForGooglePla;

    return {
      hasNavInkDiscount,
      isAdgPromo,
      isAmsterdam,
      isAplInternalBrandsButAmsterdamAndWindmill,
      isAplInternalBrandsButWindmill,
      isClearance,
      isEveryDayLowPrice,
      isOnSale,
      isWindmillCanada,
      isWindmillLoggedInUserWithCustomerPricing,
      isWindmillUserWithoutCustomerPricing,
      itemPriceCode,
      showDiscountedUnitPrice,
      showFreeQuantity,
      showSaleTotal,
      showSavings,
      showTotalCharge,
      showTotalChargeForGooglePla,
      showUnitPrice,
    };
  }
);

export const getPriceTable = createSelector(
  (state) => state.price,
  (price) => {
    const hasDiscount =
      price.hasNavInkDiscount ||
      price.isClearance ||
      (price.isCouponApplied && !!price.coupon && price.coupon.isItemLevel) ||
      price.isEveryDayLowPrice;
    const hasNavInkDiscount = price.hasNavInkDiscount;
    const isClearance = price.isClearance;
    const isEveryDayLowPrice = price.isEveryDayLowPrice;
    const isOnSale =
      price.isCouponApplied && !!price.coupon && price.coupon.isItemLevel && price.freeQuantityRatio === 0;

    return price.priceTable.map((row) => {
      const freeQuantity = Math.floor(row.quantity * price.freeQuantityRatio);
      const discount = row.unitPriceIsNotAvailable
        ? NumericValueOfNotAvailable
        : hasDiscount
        ? freeQuantity > 0
          ? row.unitPrice * freeQuantity
          : (row.unitPrice - row.discountedUnitPrice) * row.quantity
        : 0;
      const discountedTotalCharge = row.unitPriceIsNotAvailable
        ? NumericValueOfNotAvailable
        : row.discountedUnitPrice * row.quantity;
      const discountedUnitPrice = row.discountedUnitPrice;
      const quantity = row.quantity;
      const regularTotalCharge = row.unitPriceIsNotAvailable
        ? NumericValueOfNotAvailable
        : row.unitPrice * row.quantity;
      const sellTotalCharge =
        hasNavInkDiscount || isClearance || isEveryDayLowPrice || isOnSale ? discountedTotalCharge : regularTotalCharge;
      const sellUnitPrice =
        hasNavInkDiscount || isClearance || isEveryDayLowPrice || isOnSale ? row.discountedUnitPrice : row.unitPrice;
      const unitPrice = row.unitPrice;

      return {
        discount,
        discountedTotalCharge,
        discountedUnitPrice,
        freeQuantity,
        quantity,
        regularTotalCharge,
        sellTotalCharge,
        sellUnitPrice,
        unitPrice,
      };
    });
  }
);

export const getDiscountedTotalCharges = createSelector(getPriceTable, (priceTable) =>
  priceTable.map((row) => row.discountedTotalCharge)
);

export const getDiscountedUnitPrices = createSelector(getPriceTable, (priceTable) =>
  priceTable.map((row) => row.discountedUnitPrice)
);

export const getDiscounts = createSelector(getPriceTable, (priceTable) => priceTable.map((row) => row.discount));

export const getFreeQuantities = createSelector(getPriceTable, (priceTable) =>
  priceTable.map((row) => row.freeQuantity)
);

export const getQuantities = createSelector(getPriceTable, (priceTable) => priceTable.map((row) => row.quantity));

export const getSellTotalCharges = createSelector(getPriceTable, (priceTable) =>
  priceTable.map((row) => row.sellTotalCharge)
);

export const getSellUnitPrices = createSelector(getPriceTable, (priceTable) =>
  priceTable.map((row) => row.sellUnitPrice)
);

export const getTotalCharges = createSelector(
  getPriceTable,
  (_, tntMode) => tntMode,
  (priceTable, tntMode) => priceTable.map((row) => (tntMode ? row.regularTotalCharge : row.sellTotalCharge))
);

export const getUnitPrices = createSelector(getPriceTable, (priceTable) => priceTable.map((row) => row.unitPrice));

export const getMinimumQuantity = createSelector(getQuantities, (quantities) =>
  quantities.length > 0 ? Math.min(...quantities) : NumericValueOfNotAvailable
);

export const getRawPriceTable = createSelector(
  (state) => state.price,
  (price) => {
    return Object.keys(price).includes('priceTable') ? price['priceTable'] : [];
  }
);

export const getPrice = createSelector(
  (state) => state.price,
  (price) => price
);

export const getRawPriceTableValidation = createSelector(getRawPriceTable, (priceTable) => {
  return emptyPriceTableCheck(priceTable);
});
